import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import type { User } from "../types";
import { AppRail } from "./AppRail";
import { ChannelAlerts } from "./ChannelAlerts";
import { CommandPaletteProvider, useCommandPalette } from "../lib/command-palette";
import { PageHeaderSlotProvider } from "./PageHeader";
import { cn } from "./ui/cn";
import { useIsDesktop } from "../lib/media";
import { NavDrawerContext } from "../lib/nav-drawer";
import { NotificationBell } from "./NotificationBell";
import { Popover } from "./ui/Popover";
import { UserMenu } from "./UserMenu";

/**
 * The frame every NetXL app inside this bundle renders in: the platform rail,
 * the app's own navigation, then the active view.
 *
 * Extracted from `Shell` when the CRM arrived, and the extraction is the point:
 * the off-canvas drawer, the escape handling, the close-on-navigate and the
 * mobile brand header are fiddly and easy to get subtly wrong. Two copies would
 * drift, and the drift would show up as "the phone menu behaves differently in
 * the CRM", which nobody would report as a bug.
 *
 * The sidebar is a prop rather than a route: it belongs to the app, not to the
 * screen, and it must not remount when the screen inside it changes.
 */
export function AppFrame({
  sidebar,
  title,
  connected,
  toolbar,
  user,
}: {
  /**
   * The app's own navigation, or nothing.
   *
   * Optional because configuration has none to offer. Settings arrived inside
   * the Helpdesk's shell and so inherited its view list -- Unassigned, My
   * mentions, All closed -- next to a page about channels and pipelines,
   * which is a column of links to somewhere else pretending to be part of the
   * screen. The rail still gets you out; the sidebar was only clutter.
   */
  sidebar?: React.ReactNode;
  /** Shown in the phone header, beside the connection dot. */
  title: string;
  connected: boolean;
  /**
   * The product's own actions, for things that belong to it rather than to
   * whichever page is open -- "New company" is wanted from the duplicate queue
   * as much as from the list.
   *
   * Rendered into the header bar beside the account menu rather than in a strip
   * of its own. Two full-width bars stacked at the top of the CRM, one holding
   * a button and one holding an avatar, would have cost eighty pixels of the
   * screen to say what fits in one row.
   *
   * Outside the Outlet so it survives navigation, and the bar is a row of its
   * own in the grid so the screen below it still resolves its own height.
   */
  toolbar?: React.ReactNode;
  /**
   * Who is signed in. The frame needs the whole person rather than a flag: the
   * rail asks whether they are an admin, and the header bar shows them.
   */
  user: User;
}) {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  /*
   * Where the account menu lives: the white bar on a desktop, the pink one on
   * a phone. Rendered in one place or the other rather than in both and hidden
   * by CSS, because it owns state -- the open menu, the settings dialog -- and
   * two copies would be two menus that each think they are the only one.
   */
  const isDesktop = useIsDesktop();

  /*
   * State rather than a ref, because the screen below has to re-render once the
   * box exists to portal into it. A ref would hold the node and tell nobody,
   * and the heading would appear on whichever render happened next -- or never.
   *
   * The update is scheduled from the ref callback, during the commit, so React
   * flushes it before the browser paints and no frame is drawn with an empty
   * bar.
   */
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);

  /*
   * Close on navigation. Every link in the drawer leads somewhere else, and
   * leaving it open would cover the screen it just navigated to.
   */
  useEffect(() => setNavOpen(false), [location.pathname]);

  // A dialog opening closes it too -- see `lib/nav-drawer.ts`.
  const drawer = useMemo(() => ({ close: () => setNavOpen(false) }), []);

  // Escape closes it, matching every other overlay in the app.
  useEffect(() => {
    if (!navOpen) return;

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    /*
     * Outermost, so ⌘K works on every screen in every one of the four
     * products and the overlay is drawn above all of the chrome below it.
     * This is the only frame all four render inside, which is what makes one
     * mounting enough -- see `lib/command-palette.tsx`.
     */
    <CommandPaletteProvider>
    <div className="flex h-full">
      {/* Desktop: static chrome, always visible. */}
      <div className="hidden md:flex">
        <AppRail isAdmin={user.role === "admin"} />
        {sidebar}
      </div>

      {/*
        Phone: the same two components, off-canvas.

        Mounted whether open or not, and hidden with `invisible` rather than
        removed. Unmounting it took the sidebar's own state with it -- and a
        dialog the sidebar had opened, such as a new ticket, is part of that
        state, so the drawer could not close without throwing the dialog away.
        Hidden this way it stays put, and `Overlay` opts back into being seen
        (`visible`) while the drawer around it is not.
      */}
      <NavDrawerContext.Provider value={drawer}>
        <div
          className={cn(
            "fixed inset-0 z-50 flex md:hidden",
            !navOpen && "pointer-events-none invisible",
          )}
        >
          <div className="flex h-full shadow-xl">
            <AppRail isAdmin={user.role === "admin"} />
            {sidebar}
          </div>
          {/*
            The scrim is a button so dismissing works by tap and by keyboard,
            and so it carries a label instead of being an unexplained dark area
            to a screen reader.
          */}
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            className="h-full flex-1 bg-black/40"
          />
        </div>
      </NavDrawerContext.Provider>

      {/*
        Grid rather than flex: `minmax(0,1fr)` gives the content row a definite
        height, which is what lets the `h-full` layouts inside it resolve. A
        flex child with `flex-basis: 0` does not reliably do that.

        The column is `minmax(0,1fr)` for the same reason in the other axis. An
        implicit grid column is `auto`, which is as wide as the widest thing in
        it will go -- so one filter select whose longest option was a mailbox's
        full name widened the column, and with it the pink bar, the white bar
        and every page below, until the bell was off the side of the screen.
      */}
      <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] grid-rows-[auto_auto_minmax(0,1fr)] bg-surface">
        {/*
          Brand pink, like the app rail it stands in for. On a phone this bar is
          the only NetXL chrome on screen, so it carries the platform's colour
          for the same reason the rail does on a desktop -- and it reads as the
          one fixed thing above a screen whose content changes underneath it.
        */}
        <header className="flex h-12 items-center gap-1.5 bg-brand-600 pl-1.5 pr-2 md:hidden">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
            aria-expanded={navOpen}
            className={cn(
              "flex size-9 items-center justify-center rounded-md text-white",
              "hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
            )}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          <span className="truncate text-sm font-semibold text-white">
            {title}
          </span>
          {/*
            Lighter steps than the sidebar's dot uses: the SLA green and amber
            are chosen to carry on a near-white surface and all but disappear
            against brand pink. The hues stay, so the meaning is unchanged.
          */}
          <span
            title={connected ? "Live updates connected" : "Reconnecting…"}
            className={cn(
              "size-2 shrink-0 rounded-full",
              connected ? "bg-emerald-300" : "bg-amber-300",
            )}
          />

          <MobileSearch />
          {toolbar && !isDesktop ? <PhoneToolbar>{toolbar}</PhoneToolbar> : null}
          <NotificationBell tone="brand" />
          {/*
            The account menu, in this bar rather than the white one below it.
            Left in the white bar, it was the reason that bar existed at all on
            most screens -- a full-width strip holding one avatar, fifty pixels
            of every page spent on a circle -- and on the screens that did put
            something in it the avatar took the room the page's own controls
            were wrapping for.
          */}
          {isDesktop ? null : <UserMenu user={user} />}
        </header>

        {/*
          The header bar: the product's actions, then whoever is signed in.
          Always present, on every screen and every width, because the account
          menu is the only way out of the session and a bar that came and went
          with the product would hide it on the screens that have no toolbar.

          The actions are pushed right rather than led with, which is where they
          already sat inside the toolbars this row replaced -- and it keeps the
          one destructive control on the page (Sign out, inside the menu) at the
          far end, away from everything else a person clicks.
        */}
        {/*
          On a phone the bar is drawn only when it has something in it, which
          is what `:has` is asking: a page heading portalled into the slot, or
          the product's toolbar. With the account menu up in the pink bar, a
          screen with neither would otherwise keep an empty white strip across
          its top. Selected in CSS rather than tracked in state because the
          slot is filled by a portal from the screen below, and nothing up
          here is told when it is.

          Tighter on a phone, too: the same controls, less air round them.
        */}
        {/*
          The bar and the channel alerts below it share one grid row, so a
          banner coming and going never changes which row the screen lands in.
        */}
        <div>
          <div
            className={cn(
              "flex items-center gap-2 border-b border-line px-3 py-1.5 md:px-4 md:py-2",
              "max-md:[&:not(:has(>:not(:empty)))]:hidden",
            )}
          >
            {/*
              The screen's own heading, filled by portal -- see `PageHeader`.
              `flex-wrap` because a phone cannot hold a view name, a count and two
              filters on one line and the alternative was hiding the filters on
              the width that most needs them narrowed. A screen that wraps should
              do it deliberately -- the filters as one full-width second line --
              rather than leave each control to fall where it fits, which is the
              staircase this used to draw.

              Empty on a screen that renders no `PageHeader`, and the toolbar then
              sits where it always did: this box still takes the free space, so
              the product's actions stay against the account menu.
            */}
            <div
              ref={setHeaderSlot}
              className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
            />
            {/* `empty:hidden` so a product with no toolbar leaves no gap -- which
                on a phone is always, since there it is in the pink bar. */}
            <div className="flex shrink-0 items-center gap-2 empty:hidden">
              {isDesktop ? toolbar : null}
            </div>
            {isDesktop ? <UserMenu user={user} /> : null}
          </div>
          <ChannelAlerts user={user} />
        </div>

        {/*
          `min-w-0` as well as `min-h-0`, and for the same reason in the other
          axis: a grid item defaults to `min-width: auto`, which means it grows
          to its content's minimum rather than being held to its column. One
          native select whose widest option was "Time to first response" was
          enough to push a phone's whole dashboard 148px sideways -- the page
          did nothing wrong, the frame simply never said no.
        */}
        <main className="min-h-0 min-w-0">
          <PageHeaderSlotProvider value={headerSlot}>
            <Outlet />
          </PageHeaderSlotProvider>
        </main>
      </div>
    </div>
    </CommandPaletteProvider>
  );
}

/**
 * The way into the platform search on a phone.
 *
 * ⌘K is the only way in on every screen, and a phone has no ⌘K. The one button
 * that opened the palette lived in the CRM's sidebar -- so search was a
 * keyboard shortcut in three of the four products, and in the fourth it was
 * behind the off-canvas nav. On a phone it was reachable nowhere.
 *
 * A field rather than a magnifier, and it takes the free space in the bar. An
 * icon would have cost less room, but "where do I search" is the question
 * being answered and a box that says Search answers it without being learned.
 * It is a button, not an `input`: the palette brings its own box and focuses it
 * on open, and two search fields where one has to hand its text to the other is
 * a thing to keep out of a bar this size.
 *
 * Its own component because `useCommandPalette` has to be read *inside* the
 * provider, and `AppFrame` renders it -- a hook called in the frame's own body
 * would silently get the default context, whose `open` does nothing.
 */
function MobileSearch() {
  const search = useCommandPalette();

  return (
    <button
      type="button"
      onClick={search.open}
      aria-label="Search Pulse"
      className={cn(
        /* `ml-auto` so it takes what the title and the status dot leave, and
           `min-w-0` so it is the thing that shrinks on a narrow phone rather
           than pushing the bell off the end. */
        "ml-auto flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1.5",
        "bg-white/15 text-white/85 hover:bg-white/25 hover:text-white",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60",
      )}
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="shrink-0"
        aria-hidden
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <span className="truncate text-xs">Search</span>
    </button>
  );
}

/**
 * The product's toolbar on a phone: a "+" in the pink bar that opens it.
 *
 * In the white bar it cost a full-width row on every screen of the CRM, Sales
 * calls and Meetings to hold one or two buttons against the right edge --
 * forty pixels of each page to offer "New call", which nobody is pressing on
 * most of the visits that pay for it. A "+" says "make a new one" without
 * being learned, and everything the toolbar held is one tap behind it, still
 * the same buttons with the same handlers: the frame is not told what the
 * actions are and does not need to be.
 *
 * Drawn as menu entries rather than as the buttons they are -- a pink
 * full-width button in a small panel was the loudest thing on the screen, for
 * an action somebody had already chosen by opening the menu. The classes below
 * restyle whatever buttons the product passes, at a thumb's height, so the
 * menu matches the account menu beside it. Reversed, because a toolbar puts its main action last, against the account
 * menu, and in a list the main action belongs at the top. Any click inside
 * closes it -- every action here opens a dialog, and a menu left open behind
 * that dialog would still be there when it shut.
 */
function PhoneToolbar({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label="New"
      side="bottom"
      align="right"
      triggerClassName={cn(
        "flex size-9 shrink-0 items-center justify-center rounded-md text-white",
        "hover:bg-white/15 focus-visible:ring-white/60",
      )}
      className="w-48 py-1"
      trigger={
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      }
    >
      <div
        onClick={() => setOpen(false)}
        className={cn(
          "flex flex-col-reverse",
          "[&_button]:h-10 [&_button]:w-full [&_button]:justify-start [&_button]:rounded-none",
          "[&_button]:border-0 [&_button]:bg-transparent [&_button]:px-3",
          "[&_button]:text-sm [&_button]:font-normal [&_button]:text-content",
          "[&_button:hover]:bg-surface-2",
        )}
      >
        {children}
      </div>
    </Popover>
  );
}
